import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Loader,
  ScrollArea
} from "@devhop/ui";
import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { BranchProductWithDetails } from "../../domain/types";
import {
  useFindVariantByOptions,
  useProductOptions,
  useVariantModifiers,
} from "../api/queries";
import { useCart } from "../context/CartContext";
import { usePOSContext } from "../context/POSContext";
import { playFlyToCartAnimation } from "../lib/cart-add-animation";

type ProductConfigDialogProps = {
  product: BranchProductWithDetails | null;
  open: boolean;
  onClose: () => void;
  onAddItem?: (
    item: Omit<import("../context/CartContext").CartItem, "id" | "totalPrice">,
  ) => void;
};

export function ProductConfigDialog({
  product,
  open,
  onClose,
  onAddItem,
}: ProductConfigDialogProps) {
  const { selection } = usePOSContext();
  const branchId = selection?.branchId ?? null;
  const cartContext = useCart();
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const [addState, setAddState] = useState<"idle" | "success">("idle");

  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, string>
  >({});
  const [selectedModifiers, setSelectedModifiers] = useState<
    Record<string, string[]>
  >({}); // modifierId -> optionValue[]
  const [quantity, setQuantity] = useState(1);

  const { data: options, isLoading: optionsLoading } = useProductOptions(
    product?.id ?? null,
    branchId,
  );

  // Check if all options are selected
  const allOptionsSelected =
    !options || options.length === 0
      ? true
      : options.every(
          (opt) => selectedOptions[opt.id] && selectedOptions[opt.id] !== "",
        );

  // Get option value IDs for finding variant
  const optionValueIds = options
    ? Object.values(selectedOptions).filter((id) => id && id !== "")
    : [];
  const shouldResolveVariant = Boolean(options) && allOptionsSelected;

  const { data: foundVariant, isFetching: isFindingVariant } =
    useFindVariantByOptions(
      product?.id ?? null,
      branchId,
      shouldResolveVariant ? optionValueIds : [],
      shouldResolveVariant,
    );

  const { data: modifiers, isFetching: modifiersLoading } = useVariantModifiers(
    foundVariant?.id ?? null,
    branchId,
  );

  // Reset when product changes
  useEffect(() => {
    if (product) {
      setSelectedOptions({});
      setSelectedModifiers({});
      setQuantity(1);
      setAddState("idle");
    }
  }, [product?.id]);

  // Auto-select default modifier options
  useEffect(() => {
    if (modifiers) {
      const newModifiers: Record<string, string[]> = {};
      for (const vm of modifiers) {
        const defaultOption = vm.modifier.options.find((opt) => opt.isDefault);
        if (defaultOption) {
          if (vm.modifier.type === "single") {
            newModifiers[vm.modifierId] = [defaultOption.value];
          } else {
            newModifiers[vm.modifierId] = [defaultOption.value];
          }
        } else if (vm.required && vm.modifier.options.length > 0) {
          // If required and no default, select first option
          if (vm.modifier.type === "single") {
            const firstOption = vm.modifier.options[0];
            if (firstOption) {
              newModifiers[vm.modifierId] = [firstOption.value];
            }
          }
        }
      }
      setSelectedModifiers((prev) => ({ ...prev, ...newModifiers }));
    }
  }, [modifiers]);

  const canAddToCart =
    foundVariant &&
    foundVariant.price !== null &&
    allOptionsSelected &&
    (!modifiers ||
      modifiers.every(
        (vm) =>
          !vm.required || (selectedModifiers[vm.modifierId]?.length ?? 0) > 0,
      ));

  const handleOptionChange = (optionId: string, valueId: string) => {
    setSelectedOptions((prev) => ({ ...prev, [optionId]: valueId }));
  };

  const handleModifierChange = (
    modifierId: string,
    optionValue: string,
    isMultiple: boolean,
  ) => {
    setSelectedModifiers((prev) => {
      const current = prev[modifierId] || [];
      if (isMultiple) {
        const newValues = current.includes(optionValue)
          ? current.filter((v) => v !== optionValue)
          : [...current, optionValue];
        return { ...prev, [modifierId]: newValues };
      }
      return { ...prev, [modifierId]: [optionValue] };
    });
  };

  const calculateTotalPrice = (): number => {
    if (!foundVariant || foundVariant.price === null) return 0;

    const basePrice = foundVariant.price;
    let modifierAdjustments = 0;

    if (modifiers) {
      for (const vm of modifiers) {
        const selectedValues = selectedModifiers[vm.modifierId] || [];
        if (selectedValues.length > 0) {
          // Sum up prices from selected modifier options
          for (const value of selectedValues) {
            const option = vm.modifier.options.find(
              (opt) => opt.value === value,
            );
            if (option?.price) {
              modifierAdjustments += option.price;
            }
          }
        }
      }
    }

    return (basePrice + modifierAdjustments) * quantity;
  };

  const handleAddToCart = () => {
    if (!product || !foundVariant || foundVariant.price === null) return;
    if (addState === "success") return;

    const cartModifiers =
      modifiers
        ?.filter((vm) => (selectedModifiers[vm.modifierId]?.length ?? 0) > 0)
        .flatMap((vm) => {
          const selectedValues = selectedModifiers[vm.modifierId] || [];
          return selectedValues.map((value) => {
            const option = vm.modifier.options.find(
              (opt) => opt.value === value,
            );
            return {
              modifierId: vm.modifierId,
              modifierName: vm.modifier.name,
              optionValue: value,
              optionLabel: option?.label || value,
              priceDelta: option?.price ?? 0,
            };
          });
        }) || [];

    const cartItem = {
      productId: product.id,
      productName: product.name,
      variantId: foundVariant.id,
      variantName: foundVariant.name,
      price: foundVariant.price ?? 0,
      quantity,
      modifiers: cartModifiers,
      selectedOptions,
    };

    if (onAddItem) {
      onAddItem(cartItem);
      playFlyToCartAnimation(addButtonRef.current, quantity);
    } else {
      cartContext.addItem(cartItem, { animationSource: addButtonRef.current });
    }

    setAddState("success");
    // toast.success(
    //   quantity > 1
    //     ? `ເພີ່ມ ${product.name} x${quantity} ໃສ່ກະຕ່າແລ້ວ`
    //     : `ເພີ່ມ ${product.name} ໃສ່ກະຕ່າແລ້ວ`,
    // );

    window.setTimeout(() => {
      setAddState("idle");
      onClose();
    }, 450);
  };

  if (!product) {
    return null;
  }

  const isInitialLoading = optionsLoading && !options;
  const showVariantLoading =
    shouldResolveVariant && isFindingVariant && !foundVariant;
  const showModifiersLoading =
    Boolean(foundVariant?.id) && modifiersLoading && !modifiers;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl overflow-y-auto pb-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <ScrollArea className="max-h-[90vh] pr-3">
          <DialogHeader>
            <DialogTitle>{product.name}</DialogTitle>
          </DialogHeader>

          {isInitialLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Options Selection */}
              {options && options.length > 0 && (
                <div>
                  <Label className="mb-3 block font-medium">ຕົວເລືອກ</Label>
                  <div className="space-y-4">
                    {options.map((option) => (
                      <div key={option.id}>
                        <Label className="mb-2 block text-sm">
                          {option.name}
                          <span className="ml-1 text-destructive">*</span>
                        </Label>
                        <div className="grid grid-cols-3 gap-2">
                          {option.values.map((value) => {
                            const isSelected =
                              selectedOptions[option.id] === value.id;
                            return (
                              <Button
                                key={value.id}
                                variant={isSelected ? "default" : "outline"}
                                className={cn(
                                  "h-auto p-3 text-left",
                                  isSelected &&
                                    "bg-primary text-primary-foreground hover:bg-primary/90",
                                )}
                                onClick={() =>
                                  handleOptionChange(option.id, value.id)
                                }
                              >
                                <span className="font-medium">
                                  {value.value}
                                </span>
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  {!allOptionsSelected && (
                    <p className="mt-2 text-muted-foreground text-sm">
                      ກະລຸນາເລືອກຕົວເລືອກທັງໝົດກ່ອນ
                    </p>
                  )}
                  {allOptionsSelected && showVariantLoading && (
                    <div className="mt-2 flex items-center gap-2 text-muted-foreground text-sm">
                      <Loader className="h-4 w-4" />
                      ກຳລັງຄົ້ນຫາຮູບແບບ...
                    </div>
                  )}
                  {allOptionsSelected && foundVariant && (
                    <div className="mt-2 rounded-md bg-muted p-2 text-sm">
                      <span className="text-muted-foreground">ຮູບແບບ: </span>
                      <span className="font-medium">{foundVariant.name}</span>
                      {foundVariant.price !== null && (
                        <span className="ml-2 font-semibold text-primary">
                          {foundVariant.price.toLocaleString("lo-LA")} ₭
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Modifiers Selection */}
              {showModifiersLoading && (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader className="h-4 w-4" />
                  ກຳລັງໂຫຼດການປັບແຕ່ງ...
                </div>
              )}
              {modifiers && modifiers.length > 0 && (
                <div>
                  <Label className="mb-3 block font-medium">ການປັບແຕ່ງ</Label>
                  <div className="space-y-4">
                    {modifiers.map((vm) => (
                      <div key={vm.id}>
                        <Label className="mb-2 block text-sm">
                          {vm.modifier.name}
                          {vm.required && (
                            <span className="ml-1 text-destructive">*</span>
                          )}
                        </Label>
                        {vm.modifier.type === "single" ? (
                          <div className="grid grid-cols-2 gap-2">
                            {vm.modifier.options.map((opt) => {
                              const isSelected =
                                selectedModifiers[vm.modifierId]?.[0] ===
                                opt.value;
                              return (
                                <Button
                                  key={opt.id}
                                  variant={isSelected ? "default" : "outline"}
                                  className={cn(
                                    "h-auto justify-between p-3 text-left",
                                    isSelected &&
                                      "bg-primary text-primary-foreground hover:bg-primary/90",
                                  )}
                                  onClick={() =>
                                    handleModifierChange(
                                      vm.modifierId,
                                      opt.value,
                                      false,
                                    )
                                  }
                                >
                                  <span className="font-medium">
                                    {opt.label}
                                  </span>
                                  {opt.price !== 0 && (
                                    <span className="ml-2 text-xs">
                                      {opt.price > 0 ? "+" : ""}
                                      {opt.price.toLocaleString("lo-LA")} ₭
                                    </span>
                                  )}
                                </Button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {vm.modifier.options.map((opt) => {
                              const isChecked = selectedModifiers[
                                vm.modifierId
                              ]?.includes(opt.value);
                              return (
                                <Button
                                  key={opt.id}
                                  variant={isChecked ? "default" : "outline"}
                                  className={cn(
                                    "h-auto justify-between p-3 text-left",
                                    isChecked &&
                                      "bg-primary text-primary-foreground hover:bg-primary/90",
                                  )}
                                  onClick={() =>
                                    handleModifierChange(
                                      vm.modifierId,
                                      opt.value,
                                      true,
                                    )
                                  }
                                >
                                  <span className="font-medium">
                                    {opt.label}
                                  </span>
                                  {opt.price !== 0 && (
                                    <span className="ml-2 text-xs">
                                      {opt.price > 0 ? "+" : ""}
                                      {opt.price.toLocaleString("lo-LA")} ₭
                                    </span>
                                  )}
                                </Button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="sticky bottom-0 flex flex-col! gap-2 bg-background py-4">
            {/* Quantity */}
            <div className="ml-auto">
              <Label className="mb-2 block font-medium">ຈຳນວນ</Label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                >
                  -
                </Button>
                <span className="w-12 text-center font-medium">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity((q) => q + 1)}
                >
                  +
                </Button>
              </div>
            </div>

            {/* Total Price */}
            {/* <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">ລາຄາລວມ</span>
                <span className="font-bold text-lg text-primary">
                  {calculateTotalPrice().toLocaleString("lo-LA")} ₭
                </span>
              </div>
            </div> */}

            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                ຍົກເລີກ
              </Button>
              <Button
                ref={addButtonRef}
                className={cn(
                  "flex-1 transition-all",
                  addState === "success" &&
                    "animate-cart-button-success bg-emerald-600 text-white hover:bg-emerald-600",
                )}
                onClick={handleAddToCart}
                disabled={!canAddToCart || addState === "success"}
              >
                {addState === "success" ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    ເພີ່ມແລ້ວ!
                  </>
                ) : (
                  <>
                    ເພີ່ມໃສ່ກະຕ່າ ({calculateTotalPrice().toLocaleString("lo-LA")}{" "}
                    ₭)
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
